// Pure helpers for the day/night countdown header indicator.
//
// Rust+ AppTime gives us a snapshot of the in-game clock (0..24 float),
// the sunrise / sunset thresholds (also 0..24), a timeScale and a
// dayLengthMinutes. We poll AppTime every 30s on the worker; between
// polls the browser interpolates forward locally.
//
// The catch: those 5 fields describe a UNIFORM clock, but real (modded)
// servers run day and night at different real-time speeds. The official
// companion-app formula can't express that, so the worker MEASURES the
// per-phase rate by watching the clock and ships it in the snapshot
// (dayRate / nightRate). The browser just consumes those.
//
// NOTE: a near-identical copy of this math lives at
// apps/worker/src/day-night.ts so the `!time` chat reply (and the rate
// measurement itself, which is worker-only) can compute the same value
// server-side. The shape is small + stable; deliberate duplication beats
// spinning up a packages/* sibling for one feature. If you change one,
// change the other.

export interface AppTimeSnapshot {
  time: number;             // current in-game hour, 0..24 float
  sunrise: number;          // in-game hour, 0..24
  sunset: number;           // in-game hour, 0..24
  timeScale: number;        // game seconds per real second (raw from Rust+)
  dayLengthMinutes: number; // raw from Rust+; real minutes per game day
  capturedAtMs: number;     // ms-since-epoch at snapshot capture
  dayRate?: number;         // measured real-sec per game-hour during day
  nightRate?: number;       // measured real-sec per game-hour during night
}

/**
 * Real seconds per one in-game hour, for the phase that `atTime` falls in.
 *
 * Prefers the worker's empirically measured per-phase rate
 * (dayRate / nightRate) when present — real Rust servers run day and
 * night at different real-time speeds, which a single formula can't
 * express. The official companion-app formula
 * (2.5 × dayLengthMinutes / timeScale) is only a cold-start fallback
 * until the worker has measured this server; on an asymmetric server it
 * is badly wrong (e.g. predicts 200 s/h when day actually runs ~410 and
 * night ~75). Final fallback is vanilla 150 for bad/zero fields.
 */
export function realSecondsPerGameHour(snap: AppTimeSnapshot, atTime?: number): number {
  const t = atTime ?? snap.time;
  const day = isDayAt(t, snap.sunrise, snap.sunset);
  const measured = day ? snap.dayRate : snap.nightRate;
  if (measured && measured > 0) return measured;
  if (snap.dayLengthMinutes > 0 && snap.timeScale > 0) {
    return (2.5 * snap.dayLengthMinutes) / snap.timeScale;
  }
  return 150;
}

/**
 * Interpolate the in-game time at `nowMs` from a snapshot. Wraps at 24.
 *
 * Uses the rate of the snapshot's own phase: the elapsed window is short
 * (≤30s server-side, ≤1s client-side) so it never meaningfully crosses a
 * phase boundary. The per-phase distinction that matters for the user —
 * "how long until night" — lives in nextTransition, which re-derives the
 * rate at the interpolated time.
 */
export function interpolateGameTime(snap: AppTimeSnapshot, nowMs: number): number {
  const elapsedSec = Math.max(0, (nowMs - snap.capturedAtMs) / 1000);
  const t = (snap.time + elapsedSec / realSecondsPerGameHour(snap, snap.time)) % 24;
  return t < 0 ? t + 24 : t;
}

/** True if the given in-game hour is in the daytime band. */
export function isDayAt(time: number, sunrise: number, sunset: number): boolean {
  if (sunrise === sunset) return true; // degenerate; treat as eternal day
  if (sunrise < sunset) return time >= sunrise && time < sunset;
  // sunset wraps past midnight (unusual but possible)
  return time >= sunrise || time < sunset;
}

export interface NextTransition {
  kind: "until_day" | "until_night";
  realSeconds: number;
}

/**
 * Real-world seconds until the next sun transition. Kind reflects what
 * we're counting DOWN to: "until_night" when it's currently day (we're
 * counting to sunset), "until_day" when it's currently night.
 *
 * From the current time to the target we stay within ONE phase, so a
 * single rate applies — the measured rate for the phase we're in.
 */
export function nextTransition(snap: AppTimeSnapshot, nowMs: number): NextTransition {
  const t = interpolateGameTime(snap, nowMs);
  const day = isDayAt(t, snap.sunrise, snap.sunset);
  const target = day ? snap.sunset : snap.sunrise;
  let gameHours = target - t;
  if (gameHours <= 0) gameHours += 24;
  const realSeconds = gameHours * realSecondsPerGameHour(snap, t);
  return { kind: day ? "until_night" : "until_day", realSeconds };
}

/**
 * Compact duration string. Examples: "0s", "12s", "1m 5s", "2m", "1h 5m".
 * Caps at hours (no days) — Rust day cycles are real-time minutes, so
 * any "until next transition" value above an hour means timeScale is
 * absurdly low and the operator will notice.
 */
export function formatCountdown(realSeconds: number): string {
  const total = Math.max(0, Math.round(realSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}
